import { useEffect, useState } from 'react'

export type SocialPreviewState = {
  status: 'idle' | 'loading' | 'ready' | 'error'
  thumbnailUrl?: string
}

const previewCache = new Map<string, string>()
const previewRequests = new Map<string, Promise<string>>()

function isAllowedThumbnail(rawUrl: string) {
  const thumbnail = new URL(rawUrl)
  const allowedHost = [
    '.tiktokcdn.com',
    '.tiktokcdn-us.com',
    '.tiktokcdn-eu.com',
    '.muscdn.com',
    '.ibytedtos.com',
    '.byteimg.com',
  ].some((suffix) => thumbnail.hostname === suffix.slice(1) || thumbnail.hostname.endsWith(suffix))

  if (thumbnail.protocol !== 'https:' || !allowedHost) throw new Error('Unexpected preview host')
  return thumbnail.toString()
}

export function instagramMediaUrl(postUrl: string) {
  const post = new URL(postUrl)
  if (post.protocol !== 'https:' || !['instagram.com', 'www.instagram.com'].includes(post.hostname)) {
    throw new Error('Unexpected Instagram post host')
  }

  const match = post.pathname.match(/^\/(p|reel|tv)\/([A-Za-z0-9_-]+)/)
  if (!match) throw new Error('Unexpected Instagram post URL')
  // Instagram serves post thumbnails through /p/, including posts whose
  // public permalink uses /reel/ or the older /tv/ route.
  return `https://www.instagram.com/p/${match[2]}/media/?size=l`
}

function requestPreview(postUrl: string, platform: 'tiktok' | 'instagram') {
  const cacheKey = `${platform}:${postUrl}`
  const cached = previewCache.get(cacheKey)
  if (cached) return Promise.resolve(cached)

  const existing = previewRequests.get(cacheKey)
  if (existing) return existing

  if (platform === 'instagram') {
    const thumbnailUrl = instagramMediaUrl(postUrl)
    previewCache.set(cacheKey, thumbnailUrl)
    return Promise.resolve(thumbnailUrl)
  }

  const request = fetch(`https://www.tiktok.com/oembed?url=${encodeURIComponent(postUrl)}`)
    .then((response) => response.ok ? response.json() : Promise.reject(new Error('Preview unavailable')))
    .then((data: { thumbnail_url?: unknown }) => {
      if (typeof data.thumbnail_url !== 'string') throw new Error('Preview unavailable')
      const thumbnailUrl = isAllowedThumbnail(data.thumbnail_url)
      previewCache.set(cacheKey, thumbnailUrl)
      return thumbnailUrl
    })
    .finally(() => previewRequests.delete(cacheKey))

  previewRequests.set(cacheKey, request)
  return request
}

export function useSocialPreview(postUrl: string, platform: 'tiktok' | 'instagram', enabled = true): SocialPreviewState {
  const cacheKey = `${platform}:${postUrl}`
  const cached = previewCache.get(cacheKey)
  const [preview, setPreview] = useState<SocialPreviewState>(() => cached
    ? { status: 'ready', thumbnailUrl: cached }
    : { status: enabled ? 'loading' : 'idle' })

  useEffect(() => {
    if (!enabled) {
      setPreview((current) => current.status === 'ready' ? current : { status: 'idle' })
      return
    }

    let active = true
    setPreview(previewCache.has(cacheKey) ? { status: 'ready', thumbnailUrl: previewCache.get(cacheKey) } : { status: 'loading' })
    requestPreview(postUrl, platform)
      .then((thumbnailUrl) => {
        if (active) setPreview({ status: 'ready', thumbnailUrl })
      })
      .catch(() => {
        if (active) setPreview({ status: 'error' })
      })

    return () => { active = false }
  }, [enabled, platform, postUrl, cacheKey])

  return preview
}
